import { useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { isLoggedIn } from "../../auth/authStorage";
import { getExplorerEventByCode } from "../../api/explorerOnboardingApi";
import { explorerSignupPath } from "../../explorer/explorerProfileSync";
import { getSharedEventQuestionsPath } from "../../utils/eventShare";
import {
  mapEntrySourceToExplorerFlow,
  setExplorerContext,
} from "../../explorer/explorerSession";

/**
 * Legacy `/explorer/join` and `/explorer/signup` URLs → unified auth (`/?mode=register`).
 * Preserves event id/code in session when present in the old link.
 */
function ExplorerLegacyAuthRedirect() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { eventCode } = useParams<{ eventCode?: string }>();

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (isLoggedIn()) {
        const eventIdParam = searchParams.get("eventId");
        if (eventCode) {
          try {
            const event = await getExplorerEventByCode(eventCode);
            if (!cancelled) {
              navigate(getSharedEventQuestionsPath(event.id) || "/event-booking", {
                replace: true,
              });
            }
            return;
          } catch {
            /* fall through */
          }
        }
        const loggedInEventPath = getSharedEventQuestionsPath(eventIdParam);
        if (!cancelled) {
          navigate(loggedInEventPath || "/event-booking", { replace: true });
        }
        return;
      }

      const entrySource = (searchParams.get("entrySource") || "event").trim() || "event";
      const flow = mapEntrySourceToExplorerFlow(entrySource);
      const eventIdParam = searchParams.get("eventId");

      if (eventCode) {
        try {
          const event = await getExplorerEventByCode(eventCode);
          if (!cancelled) {
            setExplorerContext(String(event.id), flow, eventCode);
          }
        } catch {
          if (!cancelled) {
            setExplorerContext(null, flow, eventCode);
          }
        }
      } else if (eventIdParam) {
        setExplorerContext(eventIdParam, flow, null);
      } else {
        setExplorerContext(null, flow, null);
      }

      if (!cancelled) {
        navigate(explorerSignupPath(), { replace: true });
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [eventCode, navigate, searchParams]);

  return (
    <div className="d-flex justify-content-center align-items-center vh-100">
      <Spinner animation="border" variant="primary" role="status" />
    </div>
  );
}

export default ExplorerLegacyAuthRedirect;
